"use client";

import { GetAvailableSlotsQuery } from "@/__generated__/graphql";
import { ScheduleFormData, scheduleFormSchema } from "@/app/form/schema";
import { CushionRecommendations } from "@/components/sections/cushion-recommendations";
import { SheetMenu } from "@/components/sections/menu";
import { Products, ProductsButton } from "@/components/sections/products";
import { Review } from "@/components/sections/review";
import { Schedule } from "@/components/sections/schedule";
import { Success } from "@/components/sections/success";
import { Summary, SummaryButton } from "@/components/sections/summary";
import { TryBundle, TryBundleButton } from "@/components/sections/try-bundle";
import { useWidget } from "@/context/widget-context";
import { Language, useTranslations } from "@/lib";
import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { useForm } from "react-hook-form";
export interface Product {
  id: string;
  title: string;
  name: string;
  url: string;
  imageUrl: string;
  size: string;
  price: string;
  originalPrice: string;
  variant?: string;
  cushionRecommendations?: {
    id: string;
    title: string;
    url: string;
    name: string;
    imageUrl: string;
    variant: string;
    price: string;
    originalPrice: string;
    size: string;
  }[];
}

// get the lang from the html tag lang attribute
const lang = document.documentElement.lang as Language;

export default function Drawer() {
  const widget = useWidget();
  // const [lang, setLang] = useState<Language>("en");
  const { t, loading } = useTranslations(lang);
  const form = useForm<ScheduleFormData>({
    resolver: zodResolver(scheduleFormSchema),
    defaultValues: {
      region: "",
      emirate: "",
      date: undefined,
      startDateTime: "",
      duration: undefined,
      name: "",
      phone: "",
      email: "",
      address: "",
    },
  });
  const [slots, setSlots] = useState<GetAvailableSlotsQuery>();

  const renderStepContent = () => {
    switch (widget.step) {
      case 1:
        return <Products widget={widget} lang={lang} />;
      case 2:
        return <TryBundle widget={widget} lang={lang} />;
      case 3:
        return (
          <>
            <TryBundle widget={widget} lang={lang} />
            <CushionRecommendations lang={lang} widget={widget} />
          </>
        );
      case 4:
        return <Summary widget={widget} lang={lang} />;
      case 5:
        return (
          <Schedule
            form={form}
            widget={widget}
            lang={lang}
            slots={slots}
            setSlots={setSlots}
          />
        );
      case 6:
        return <Review widget={widget} form={form} lang={lang} />;
      case 7:
        return <Success lang={lang} />;
      default:
        return null;
    }
  };
  const renderTitleContent = () => {
    switch (widget.step) {
      case 6:
        return t("render_title_content.text_one");
      case 7:
        return " ";
      default:
        return null;
    }
  };
  const renderDescriptionContent = () => {
    switch (widget.step) {
      case 5:
        return (
          <>
            <span className="tw:font-karla tw:text-lg tw:font-semibold">
              {t("render_description_content.text_one")}
            </span>
          </>
        );
      case 6:
        return (
          <>
            <span className="tw:font-karla tw:text-lg tw:font-semibold">
              {t("render_description_content.text_two")}
            </span>
          </>
        );
      case 7:
        return " ";
      default:
        return null;
    }
  };
  const renderFooterContent = () => {
    switch (widget.step) {
      case 1:
        return (
          <ProductsButton
            lang={lang}
            disabled={widget.products.length === 0}
            widget={widget}
          />
        );
      case 2:
      case 3:
        return <TryBundleButton lang={lang} widget={widget} />;
      case 4:
        return <SummaryButton lang={lang} widget={widget} />;
      default:
        return null;
    }
  };
  if (loading) {
    return null;
  }

  return (
    <div>
      <SheetMenu
        sheetOpen={widget.isOpen}
        setSheetOpen={(open: boolean) => {
          if (open) {
            widget.open();
          } else {
            widget.close();
          }
        }}
        sheetTitle={renderTitleContent()}
        sheetDescription={renderDescriptionContent()}
        sheetFooter={renderFooterContent()}
        lang={lang}
      >
        {renderStepContent()}
      </SheetMenu>
    </div>
  );
}
